import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useHead } from '@unhead/react'
import { ArrowRight, Phone } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Lightbox from '@/components/Lightbox'
import PhotoUploader from '@/components/PhotoUploader'
import PhotoSliderCard from '@/components/PhotoSliderCard'
import { useList } from '@/lib/db'
import { useAuth } from '@/hooks/useAuth'
import { CONTACT, type ProjectPhoto } from '@/data/content'

interface PortfolioPhoto {
  _id: string
  imageUrl: string
  alt?: string
  fileName?: string
  order?: number
  project?: string
  projectTitle?: string
  createdAt?: string
  hidden?: boolean
}

interface ProjectGroup {
  name: string
  photos: ProjectPhoto[]
}

const EMPTY_CARDS = 3

export default function Projects() {
  useHead({
    title: 'Our Work | Trimmed Living Project Portfolio',
    meta: [
      {
        name: 'description',
        content:
          'Custom landscaping, hardscaping, and outdoor living across Central & Eastern North Carolina.',
      },
    ],
  })

  const { isAuthenticated } = useAuth()
  const { data, loading, error, refresh } = useList<PortfolioPhoto>('portfolio_photos', {
    sort: { createdAt: 1 },
    limit: 200,
  })

  const groups = useMemo<ProjectGroup[]>(() => {
    const rows = (data?.list ?? []).filter(
      photo => Boolean(photo.imageUrl) && !photo.hidden && !/^untitled/i.test(photo.alt ?? ''),
    )
    const map = new Map<string, PortfolioPhoto[]>()
    const order: string[] = []

    rows.forEach(row => {
      const name = (row.project || row.projectTitle || 'Project photos').trim()
      if (!map.has(name)) {
        map.set(name, [])
        order.push(name)
      }
      map.get(name)!.push(row)
    })

    return order.map(name => ({
      name,
      photos: (map.get(name) ?? []).map(row => ({
        src: row.imageUrl,
        alt: row.alt || row.fileName || `${name} project photo`,
        label: name,
      })),
    }))
  }, [data])

  const projectNames = groups.map(group => group.name)
  const emptyCards = Math.max(0, EMPTY_CARDS - groups.length)
  const [open, setOpen] = useState<{ card: number; index: number } | null>(null)
  const active = open ? groups[open.card] : null

  return (
    <div className="min-h-screen bg-bone-100 font-sans text-forest-950 antialiased">
      <Header />

      <main>
        <section className="bg-bone-100">
          <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8 sm:py-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium text-forest-950/55 transition hover:text-forest-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-100 sm:text-sm"
            >
              <span aria-hidden="true">&larr;</span> Back to Home
            </Link>
            <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-forest-950 sm:text-4xl">
              Our Work
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-forest-950/65 sm:text-base">
              Custom landscaping, hardscaping, and outdoor living across Central &amp; Eastern
              North Carolina.
            </p>
          </div>
        </section>

        <section className="pb-14 lg:pb-20">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            {isAuthenticated && <PhotoUploader onUploaded={refresh} projects={projectNames} />}

            {error && (
              <p
                role="alert"
                className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                Photos could not be loaded right now. {error}
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
              {loading &&
                !data &&
                Array.from({ length: EMPTY_CARDS }).map((_, i) => (
                  <div
                    key={`loading-${i}`}
                    className="aspect-[4/3] w-full animate-pulse rounded-2xl border border-bone-300 bg-forest-950/10"
                  />
                ))}

              {groups.map((group, cardIndex) => (
                <PhotoSliderCard
                  key={group.name}
                  photos={group.photos}
                  title={group.name}
                  priority={cardIndex < 3}
                  onExpand={index => setOpen({ card: cardIndex, index })}
                />
              ))}

              {!loading &&
                emptyCards > 0 &&
                Array.from({ length: emptyCards }).map((_, i) => (
                  <PhotoSliderCard
                    key={`empty-${i}`}
                    photos={[]}
                    title="Empty photo slot"
                    onExpand={() => undefined}
                  />
                ))}
            </div>
          </div>
        </section>

        <section className="border-t border-bone-300 bg-bone-100">
          <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-5 py-10 sm:px-8 sm:py-12 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:py-14">
            <h2 className="max-w-xl font-display text-2xl font-semibold leading-tight tracking-tight text-forest-950 sm:text-3xl">
              Ready to Transform Your Outdoor Space?
            </h2>

            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Link
                to="/#contact"
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-bone-50 transition hover:bg-pine-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-100"
              >
                Request a Free Estimate
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </Link>
              <a
                href={CONTACT.phoneHref}
                className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-forest-950/20 px-6 py-3.5 text-sm font-semibold tabular-nums text-forest-950 transition hover:border-forest-950/40 hover:bg-bone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-100"
              >
                <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">{CONTACT.phone}</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {open && active && (
        <Lightbox
          photos={active.photos}
          index={open.index}
          title={active.name}
          onIndex={next => setOpen({ card: open.card, index: next })}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}
